import { Field, FieldArray, Form } from 'formik';
import { TFunction } from 'i18next';
import React, { FC, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { stepExportFulfilled } from '../../../../../actions/registerShipmentActions';
import { getCurrentLangPretty } from '../../../../../helpers/url/urlHelpers';
import useCountries from '../../../../../hooks/useCountries';
import useScrollToFirstFormikError from '../../../../../hooks/useScrollToFIrstFormikError';
import IPageViewModel from '../../../../../interfaces/umbracoNodes/pageViewModel';
import Button from '../../../../base/button';
import FormikCheckbox from '../../../../base/forms/formikCheckbox';
import FormikFieldWrapper from '../../../../base/forms/formikFieldWrapper';
import FormikRadio from '../../../../base/forms/formikRadio';
import FormikRadioWrapper from '../../../../base/forms/formikRadioWrapper';
import FormikSelect from '../../../../base/forms/formikSelect';
import { Column, Columns } from '../../../../base/grid';
import { A } from '../../../../base/typography';
import { IStepExportFormInfo, stepExportInitialValues } from '../stepExport.schema';
import CustomsInformation from '../customsInformation';
import {
	computeLineValue,
	currencySelectOptions,
	documentsContentLine,
	DOCUMENTS_HS_CODE,
	getPackageTypeSelectOptions,
	getReturnPolicies,
	isItemWeightRequired,
} from './helpers';
import * as StyledStepExportForm from './stepExportForm.styles';
import { CustomsInformationWrapper } from './stepExportForm.styles';

interface Props {
	values: IStepExportFormInfo;
	setFieldTouched: any;
	setFieldValue: any;
	isValid: boolean;
	isSubmitting: boolean;
	onPrevious: any;
	errors: any;
	page: IPageViewModel;
}

const StepExportForm: FC<Props> = ({
	values,
	setFieldTouched,
	setFieldValue,
	errors,
	isSubmitting,
	isValid,
	onPrevious,
	page,
}) => {
	const dispatch = useDispatch<any>();
	const { t } = useTranslation('registerShipment');

	const ref = useRef(null);
	const returnPolicies = getReturnPolicies(t);
	const { stepTypeFormInfo, stepShipmentFormInfo, stepInfoFormInfo } = useSelector(
		({ registerShipmentReducer }) => registerShipmentReducer
	);

	const isInternationalLetter = stepShipmentFormInfo?.serviceOption === 'InternationalLetter';
	useScrollToFirstFormikError(errors, isSubmitting, isValid, ref);
	const { service } = stepTypeFormInfo;
	const { country } = stepInfoFormInfo;
	const lang = getCurrentLangPretty();
	const { countries } = useCountries(lang, true);

	const isUSAorPR = country?.value === 'US' || country?.value === 'PR';
	const shouldHidePrice = isUSAorPR && values.packageType?.value === 'D'; // if Documents is selected and destination is USA or PR
	// Documents-only shipments (international letters, or USA/PR with package type 'D') ship a single
	// hardcoded documents line and disable the customs item editing/lookup, same as letters.
	const forcedDocuments = isInternationalLetter || shouldHidePrice;
	const showWeight = isItemWeightRequired(country?.value, values.packageType?.value, isInternationalLetter);
	const packageTypeSelectOptions = getPackageTypeSelectOptions(
		t,
		isUSAorPR,
		!!country?.requiresBusinessAccountForCompanySender
	);
	// Saving the user's input if he decides to go back a step here
	const currencySelectOptionsFunction = currencySelectOptions(isUSAorPR);
	const onPreviousClick = () => {
		dispatch(stepExportFulfilled(values));
		onPrevious();
	};

	// Keep the derived line value (Verðmæti = unitPrice × quantity) in sync. When the price is
	// hidden (USA/PR documents) or this is an international letter, the value is forced to '0'.
	useEffect(() => {
		const updatedContents = values.contents.map(contentLine => ({
			...contentLine,
			price:
				shouldHidePrice || isInternationalLetter
					? '0'
					: computeLineValue(contentLine.unitPrice, contentLine.amountInPackage),
		}));

		const needsUpdate = values.contents.some(
			(contentLine, idx) => contentLine.price !== updatedContents[idx].price
		);
		if (needsUpdate) {
			setFieldValue('contents', updatedContents);
		}
	}, [shouldHidePrice, setFieldValue, values.contents, isInternationalLetter]);

	// Hardcode the single documents line when this becomes a documents-only shipment (USA/PR +
	// Documents chosen live here), and clear it back to a blank line when it stops being one, so it
	// behaves exactly like the fixed international-letter documents line.
	const contentsIsHardcodedDocuments =
		values.contents.length === 1 && values.contents[0]?.hsTariffCode === DOCUMENTS_HS_CODE;
	useEffect(() => {
		if (forcedDocuments) {
			if (!contentsIsHardcodedDocuments) {
				setFieldValue('contents', [{ ...documentsContentLine }]);
			}
		} else if (contentsIsHardcodedDocuments) {
			// Was showing the hardcoded documents line but is no longer a documents shipment — reset.
			setFieldValue('contents', [{ ...stepExportInitialValues.contents[0] }]);
		}
	}, [forcedDocuments, contentsIsHardcodedDocuments, setFieldValue]);

	return (
		<div ref={ref}>
			<Form>
				<StyledStepExportForm.Wrapper>
					<Columns>
						<Column tablet={1 / 3}>
							<FormikFieldWrapper
								label={`${t('step-export.package')} *`}
								name="packageType"
								smallLabel
								languageComponent="registerShipment">
								<FormikSelect
									id="packageType"
									name="packageType"
									value={values.packageType}
									options={packageTypeSelectOptions}
									disabled={isInternationalLetter}
									placeholder={t('step-export.package-choose')}
									onBlur={setFieldTouched}
									onChange={setFieldValue}
									aria-label="package-type"
								/>
							</FormikFieldWrapper>
						</Column>
						<Column tablet={1 / 3}>
							<FormikFieldWrapper
								label={`${t('step-export.returnPolicy')} *`}
								name="returnPolicy"
								smallLabel
								languageComponent="registerShipment">
								<FormikSelect
									id="returnPolicy"
									name="returnPolicy"
									value={values.returnPolicy}
									options={returnPolicies}
									placeholder={t('step-export.chooseFromList')}
									onBlur={setFieldTouched}
									onChange={setFieldValue}
								/>
							</FormikFieldWrapper>
						</Column>
						<Column tablet={1 / 3}>
							<FormikFieldWrapper
								label={`${t('step-export.currency')} *`}
								name="currency"
								smallLabel
								languageComponent="registerShipment">
								<FormikSelect
									id="currency"
									name="currency"
									disabled={isUSAorPR}
									value={values.currency}
									options={currencySelectOptionsFunction}
									placeholder={t('step-export.currency-choose')}
									onBlur={setFieldTouched}
									onChange={setFieldValue}
									aria-label="currency"
								/>
							</FormikFieldWrapper>
						</Column>
					</Columns>
					<CustomsInformationWrapper>
						<CustomsInformation
							// Remount when documents-mode toggles so the lookup/card state re-initialises.
							key={forcedDocuments ? 'documents' : 'items'}
							forcedDocuments={forcedDocuments}
							isUSAorPR={isUSAorPR}
							shouldHidePrice={shouldHidePrice}
							showWeight={showWeight}
							recipientCountry={country.value}
							countries={countries}
						/>
					</CustomsInformationWrapper>
					<StyledStepExportForm.SectionHeading>{t('step-export.terms')}</StyledStepExportForm.SectionHeading>
					<StyledStepExportForm.DangerousContentsWrapper>
						{['CN', 'DE', 'HK', 'IT', 'JP', 'LA', 'MG'].includes(country.value) && (
							<StyledStepExportForm.LithiumWarning>
								{t('step-export.lithiumWarning')}
							</StyledStepExportForm.LithiumWarning>
						)}
            {isUSAorPR && (
              <>
                <FormikFieldWrapper name="usaTerms" languageComponent="registerShipment">
                  <FormikCheckbox
                    name="usaTerms"
                    id="usaTerms"
                    label={
                      <>
                        {t('step-export.usaTerms')}{' '}
                        <A
                          href={
                            getCurrentLangPretty() === 'is'
                              ? 'https://posturinn.is/posturinn/upplysingar/vidskiptaskilmalar/?id=PDDP-sendingar%20fr%C3%A1%20einstaklingum%20til%20Bandar%C3%ADkjanna#tab=2'
                              : 'https://posturinn.is/en/posturinn/information/terms-conditions/?id=PDDP-shipments%20from%20individuals%20to%20U.S.#tab=2'
                          }
                          target="_blank">
                          {t('step-export.usaTerms2')}
                        </A>{' '}
                        {t('step-export.usaTerms3')}
                      </>
                    }
                    setFieldValue={setFieldValue}
                  />
                </FormikFieldWrapper>
                <StyledStepExportForm.InfoWrapper>
                  <Trans
                    t={t as unknown as TFunction}
                    i18nKey="step-export.usaSenderInformation"
                    components={[<StyledStepExportForm.UsaSenderInformationEmphasis />]}
                  />
                </StyledStepExportForm.InfoWrapper>
              </>
            )}
						<FormikFieldWrapper name="shipmentHasNoDangerousContents" languageComponent="registerShipment">
							<FormikCheckbox
								name="shipmentHasNoDangerousContents"
								id="shipmentHasNoDangerousContents"
								label={
									<>
										{t('step-export.dangerousGoods')}{' '}
										<A href={page.Values.DangerousContentLink.Url} target="_blank">
											{t('step-export.dangerousGoods2')}
										</A>{' '}
										{t('step-export.dangerousGoods3')}
									</>
								}
								setFieldValue={setFieldValue}
							/>
						</FormikFieldWrapper>
						<StyledStepExportForm.InfoWrapper>
							{t('step-export.exampleOfDangerousGoods') +
								': ' +
								t('step-export.battery') +
								', ' +
								t('step-export.sprayCan') +
								', ' +
								t('step-export.perfume')}
							{/* <StyledStepExportForm.DangerousContentsListLink
								href={page.Values.DangerousContentLink.Url}
								target="_blank">
								{page.Values.DangerousContentLink.Name}
							</StyledStepExportForm.DangerousContentsListLink> */}
						</StyledStepExportForm.InfoWrapper>
					</StyledStepExportForm.DangerousContentsWrapper>
					{service === 'parcel' && (
						<StyledStepExportForm.ShippableWrapper>
							<FormikFieldWrapper
								label={t('step-export.cant-deliver')}
								name="ifProductIsNotShippable"
								languageComponent="registerShipment">
								<FormikRadioWrapper>
									<FormikRadio
										id="A"
										name="ifProductIsNotShippable"
										label={`${t('step-export.return')}`}
										setFieldValue={setFieldValue}
									/>
									<FormikRadio
										id="X"
										name="ifProductIsNotShippable"
										label={`${t('step-export.destroy')}`}
										setFieldValue={setFieldValue}
									/>
								</FormikRadioWrapper>
							</FormikFieldWrapper>
						</StyledStepExportForm.ShippableWrapper>
					)}
					<StyledStepExportForm.ButtonWrapper>
						<Button type="button" onClick={onPreviousClick}>
							{t('shared.previous-step')}
						</Button>
						<Button primary type="submit">
							{t('shared.conformation')}
						</Button>
					</StyledStepExportForm.ButtonWrapper>
				</StyledStepExportForm.Wrapper>
			</Form>
		</div>
	);
};

export default StepExportForm;
